/**
 * GraphQL queries for Schedule primitive
 *
 * 6 queries covering:
 * - Single schedule retrieval
 * - List with pagination
 * - Active schedules
 * - Life-critical schedules
 * - Due schedules (within time window)
 * - Execution summary metrics
 */

import { gql } from '@apollo/client';
import {
  SCHEDULE_FIELDS,
  SCHEDULE_CONNECTION_FIELDS,
  SCHEDULE_EXECUTION_SUMMARY_FIELDS,
} from './fragments';

/**
 * Get a single schedule by ID
 */
export const GET_SCHEDULE = gql`
  ${SCHEDULE_FIELDS}
  query GetSchedule($tenantId: String!, $scheduleId: String!) {
    getSchedule(tenantId: $tenantId, scheduleId: $scheduleId) {
      ...ScheduleFields
    }
  }
`;

/**
 * List all schedules for a tenant with pagination
 */
export const LIST_SCHEDULES = gql`
  ${SCHEDULE_CONNECTION_FIELDS}
  query ListSchedules($tenantId: String!, $limit: Int, $cursor: String) {
    listSchedules(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      ...ScheduleConnectionFields
    }
  }
`;

/**
 * Get all active schedules for a tenant
 */
export const GET_ACTIVE_SCHEDULES = gql`
  ${SCHEDULE_FIELDS}
  query GetActiveSchedules($tenantId: String!) {
    getActiveSchedules(tenantId: $tenantId) {
      ...ScheduleFields
    }
  }
`;

/**
 * Get all life-critical schedules for a tenant
 * Critical for healthcare medication schedules, safety alerts
 */
export const GET_LIFE_CRITICAL_SCHEDULES = gql`
  ${SCHEDULE_FIELDS}
  query GetLifeCriticalSchedules($tenantId: String!) {
    getLifeCriticalSchedules(tenantId: $tenantId) {
      ...ScheduleFields
    }
  }
`;

/**
 * Get schedules due within a specified time window
 * Used for proactive execution and monitoring
 */
export const GET_DUE_SCHEDULES = gql`
  ${SCHEDULE_FIELDS}
  query GetDueSchedules($tenantId: String!, $withinMinutes: Int!) {
    getDueSchedules(tenantId: $tenantId, withinMinutes: $withinMinutes) {
      ...ScheduleFields
    }
  }
`;

/**
 * Get execution summary metrics for a schedule
 * Used for performance monitoring and reliability tracking
 */
export const GET_SCHEDULE_EXECUTION_SUMMARY = gql`
  ${SCHEDULE_EXECUTION_SUMMARY_FIELDS}
  query GetScheduleExecutionSummary($tenantId: String!, $scheduleId: String!) {
    getScheduleExecutionSummary(tenantId: $tenantId, scheduleId: $scheduleId) {
      ...ScheduleExecutionSummaryFields
    }
  }
`;
